import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import MdaeDataGrid from "../common_widgets/data-grid";
import { PAYER_AUDIT_LEVEL_OPTIONS } from "./edit-or-attach-document";
import { getReleventFileSizeUnitFromBytes } from "./common-functions";

const EMPTY_CATEGORIES: any[] = [];

export interface DocumentLibraryRow {
  documentId: number;
  documentName: string;
  originalFileName: string;
  fileType: string;
  fileSize: number;
  fileSizeDisplayName: string;
  s3ObjectName: string;
  documentCategoryId: number | null;
  documentCategoryName: string;
  attachedLevelName: string;
  createdBy: string;
  createDate: string;
}

export interface LevelOption {
  code: string;
  displayName: string;
}

interface IDocumentLibraryGrid {
  documentList: DocumentLibraryRow[];
  onSelectionChanged: (selectedRows: DocumentLibraryRow[]) => void;
  showLevelSelector?: boolean;
  showCategorySelector?: boolean;
  levelOptions?: LevelOption[];
  height?: number;
}

const levelHeaderRender = () => (
  <span>Level <span style={{ color: "red" }}>*</span></span>
);

const DocumentLibraryGrid: React.FC<IDocumentLibraryGrid> = ({
  documentList,
  onSelectionChanged,
  showLevelSelector = false,
  showCategorySelector = true,
  levelOptions = PAYER_AUDIT_LEVEL_OPTIONS,
  height = 300,
}) => {
  const categories: any[] = useSelector(
    (state: any) =>
      state["documentManagementReducer"].documentCategoryList?.externalWorkflowDocumentCategories ?? EMPTY_CATEGORIES
  );

  const categoriesRef = useRef(categories);
  useEffect(() => { categoriesRef.current = categories; }, [categories]);

  const enrichRows = useCallback(
    (rows: any[]): DocumentLibraryRow[] =>
      rows.map((row) => ({
        ...row,
        documentCategoryName:
          categories.find((c) => c.documentCategoryId === row.documentCategoryId)?.documentCategoryName ??
          row.documentCategoryName ??
          "",
      })),
    [categories]
  );

  const handleSelectionChanged = useCallback(
    (e) => { onSelectionChanged(enrichRows(e.selectedRowsData)); },
    [onSelectionChanged, enrichRows]
  );

  const handleRowUpdated = useCallback(
    (e) => { onSelectionChanged(enrichRows(e.component.getSelectedRowsData())); },
    [onSelectionChanged, enrichRows]
  );

  const editingConfig = useMemo(
    () => ({ mode: "cell" as const, allowUpdating: true, allowAdding: false, allowDeleting: false }),
    []
  );

  const selectionConfig = useMemo(
    () => ({ mode: "multiple" as const, showCheckBoxesMode: "always" as const }),
    []
  );

  const visibleItemConfig = useMemo(() => ({ visible: true }), []);
  const columnChooserVisibleItemConfig = useMemo(() => ({ isMdaeColumnChooserVisible: true }), []);

  const fileSizeCellRender = useCallback(
    (e) => e.data.fileSizeDisplayName || getReleventFileSizeUnitFromBytes(e.data.fileSize) || "",
    []
  );

  const mdaeColumns = useMemo(() => [
    {
      dataField: "documentName",
      caption: "Name",
      minWidth: 150,
      allowEditing: false,
    },
    ...(showCategorySelector
      ? [
          {
            caption: "Category",
            dataField: "documentCategoryId",
            width: 175,
            showEditorAlways: true,
            editorOptions: { placeholder: "Choose a Category" },
            lookup: {
              dataSource: () => categoriesRef.current,
              displayExpr: "documentCategoryName",
              valueExpr: "documentCategoryId",
              allowClearing: true,
            },
          },
        ]
      : []),
    ...(showLevelSelector
      ? [
          {
            caption: "Level",
            dataField: "attachedLevelName",
            width: 185,
            headerCellRender: levelHeaderRender,
            showEditorAlways: true,
            editorOptions: { placeholder: "Choose a Level" },
            lookup: {
              dataSource: levelOptions,
              displayExpr: "displayName",
              valueExpr: "code",
            },
            validationRules: [{ type: "required", message: "Level is required" }],
          },
        ]
      : []),
    {
      dataField: "fileType",
      caption: "Type",
      width: 90,
      allowEditing: false,
    },
    {
      dataField: "fileSizeDisplayName",
      caption: "Size",
      width: 105,
      allowEditing: false,
      allowFiltering: false,
      cellRender: fileSizeCellRender,
    },
    {
      dataField: "createdBy",
      caption: "Uploaded By",
      minWidth: 120,
      allowEditing: false,
    },
    {
      dataField: "createDate",
      caption: "Uploaded On",
      dataType: "date",
      format: "MM/dd/yyyy",
      width: 110,
      allowEditing: false,
      sortOrder: "desc",
    },
  ], [showCategorySelector, showLevelSelector, levelOptions, fileSizeCellRender]);

  return (
    <MdaeDataGrid
      id="grdDocumentLibrary"
      dataSource={documentList}
      keyExpr="documentId"
      height={height}
      showBorders={true}
      allowColumnResizing={true}
      allowColumnReordering={true}
      mdaeSearchPanel={visibleItemConfig}
      filterRow={visibleItemConfig}
      headerFilter={visibleItemConfig}
      mdaeColumnChooser={columnChooserVisibleItemConfig}
      clearFilterButtonId="btn-document-library-grid-clear-filter"
      pageSize={10}
      editing={editingConfig}
      selection={selectionConfig}
      onSelectionChanged={handleSelectionChanged}
      onRowUpdated={handleRowUpdated}
      mdaeColumns={mdaeColumns}
    />
  );
};

export default React.memo(DocumentLibraryGrid);
